import { animated, useSpring } from '@react-spring/web';
import React, { useState } from 'react';
import { Flexbox, FlexboxColumn } from '../../../components/action/Flexbox';
import InputField from '../../../components/action/InputField';
import ButtonApi from '../../../components/ButtonApi';
import ThemedText from '../../../components/theme/ThemedText';
import ThemeLogo from '../../../components/theme/ThemeLogo';
import { types } from '../../../constants';
import { useBackendApi } from '../../../helpers/backendHelper';
import { useDiscordHelper } from '../../../helpers/discordHelper';
import { useEmailLoginHelper } from '../../../helpers/login/emailLoginHelper';
import { useTwitterHelper } from '../../../helpers/twitterHelper';
import { useGlobalState } from '../../../state/GlobalStateProvider';
import utils, { truncateEthereumAddress } from '../../../utils';

const Forms = ({ onBack, onNext, worldData }) => {
  const [{ user, mercleCommunity, refreshUserData, setUser }] = useGlobalState();
  const { loginInvasive } = useEmailLoginHelper();
  const discordHelper = useDiscordHelper(mercleCommunity);
  const twitterHelper = useTwitterHelper(mercleCommunity);
  const [country, setCountry] = useState();
  const [state, setState] = useState();
  const [email, setEmail] = useState(user?.email);
  const [discordConnecting, setDiscordConnecting] = useState(false);
  const [name, setName] = useState();
  const [twitterConnecting, setTwitterConnect] = useState();
  const backendApi = useBackendApi();
  const [cities, setCities] = useState();
  const [emailConnecting, setEmailConnecting] = useState();

  const springProps = useSpring({
    from: { transform: 'translateX(80%)', opacity: 0 },
    to: { transform: 'translateX(0)', opacity: 1 },
    config: { tension: 150, friction: 30 },
    width: '100%',
    height: '100%',
  });

  function handleCaptchaChange(value) {
    console.log('captacha value', value);
  }

  const sendMagicLink = async () => {
    if (emailConnecting) {
      return refreshUserData();
    }
    if (!email || !utils.isValidEmail(email)) return alert.error('Invalid email');

    try {
      await loginInvasive({ email, postLoginAction: types.postLoginActions.none });
      setEmailConnecting(true);
    } catch (e) {}
  };

  async function handleDiscordConnect() {
    if (user?.discord?.id) return;

    if (discordConnecting) {
      return refreshUserData();
    }

    discordHelper.loginDiscord();
    setDiscordConnecting(true);
  }

  async function handleTwitterConnect() {
    if (user?.twitter?.username) return;

    if (twitterConnecting) {
      return refreshUserData();
    }
    twitterHelper.loginTwitter(`${window.location.href}closetab`);
    setTwitterConnect(true);
  }

  const disconnect = async id => {
    try {
      setDiscordConnecting(false);
      setTwitterConnect(false);
      setEmailConnecting(false);
      const promise = backendApi.user.disconnectAccount({ loginVia: id });
      const newUser = await alert.promise(promise, {
        pending: `Disconnecting`,
        success: `disconnected`,
        error: `Could not disconnect account`,
      });
      setUser(newUser);
    } catch (e) {}
  };

  async function handleSubmit() {
    try {
      if (!name) return alert.error('Name is  missing');
      if (!country || !state) return alert.error('Location is missing');
      if (!user?.email && !user?.twitter?.username && !user?.discord?.id) return alert.error('Connect atleast one Social profile');

      const payload = {
        seedApplicant: true,
        application: 'Whitelist',
        fullname: name,
        country: String(country).split('_')?.[0],
        city: state,
        address: user?.address,
        continent: worldData?.continent,
      };

      await backendApi.saveWhitelist(payload);
      onNext();
    } catch (error) {
      console.log('Something went wrong');
    }
  }

  function handleCountryChange(event) {
    const { value } = event.target;
    const index = Number(value.split('_')?.[1]);
    setCountry(value);
    setCities(worldData?.countries[index]?.topcities);
  }

  return (
    <animated.div style={springProps} className={'form_container'}>
      <div className='back_button' onClick={onBack}>
        <svg xmlns='http://www.w3.org/2000/svg' width='4' height='7' viewBox='0 0 4 7' fill='none'>
          <path
            fill-rule='evenodd'
            clip-rule='evenodd'
            d='M0.159041 3.12961L3.23192 0.166585L4 0.907209L1.31117 3.49992L4 6.09263L3.23192 6.83325L0.159041 3.87023C0.0572073 3.77201 -1.46664e-08 3.63881 -1.46664e-08 3.49992C-1.46664e-08 3.36103 0.0572073 3.22783 0.159041 3.12961Z'
            fill='black'
            fill-opacity='0.5'
          />
        </svg>
        Back
      </div>
      <Flexbox gap='0.5rem'>
        <ThemeLogo icon={'user'} />
        <ThemedText.h4 weight='medium'>Applicant Details</ThemedText.h4>
      </Flexbox>
      <FlexboxColumn gap='0.25rem' alignItems={'flex-start'}>
        <ThemedText.p>Connected Wallet</ThemedText.p>
        <ThemedText.h5 weight='semi-bold'>{truncateEthereumAddress(user?.address)}</ThemedText.h5>
      </FlexboxColumn>
      <InputField label={'Name'} placeholder={'Enter name'} onChange={e => setName(e.target.value)} value={name} />
      <FlexboxColumn fw gap='0.5rem' alignItems={'flex-start'}>
        <InputField label={'Email'} disabled={user?.email} placeholder={'Enter email'} onChange={e => setEmail(e.target.value)} value={email} />
        {!user?.email && (
          <button className='verify_email' onClick={sendMagicLink}>
            {emailConnecting ? 'Refresh' : ' Send Verification Link'}
          </button>
        )}
      </FlexboxColumn>

      <FlexboxColumn alignItems={'flex-start'} fw gap='0.5rem'>
        <ThemedText.h5>Socials</ThemedText.h5>
        <Flexbox>
          <div className='discord_button'>
            <ThemeLogo icon={'discord'} />
            <Flexbox gap='0.2rem' className='usernames ellipse' onClick={handleDiscordConnect}>
              {user?.discord?.id ? user?.discord?.id : discordConnecting ? 'Refresh' : 'Discord'}
            </Flexbox>

            {user?.discord?.id && (
              <div className='cross_button' onClick={() => disconnect(types.loginVia.discord)}>
                X
              </div>
            )}
          </div>
          <div className='twitter_button'>
            <Flexbox gap='0.2rem' className='usernames elipse' onClick={handleTwitterConnect}>
              <ThemeLogo icon={'twitterFilled'} />
              {user?.twitter?.username ? user?.twitter?.username : twitterConnecting ? 'Refresh' : 'Twitter'}
            </Flexbox>

            {user?.twitter?.username && (
              <div className='cross_button' onClick={() => disconnect(types.loginVia.twitter)}>
                X
              </div>
            )}
          </div>
        </Flexbox>
      </FlexboxColumn>

      <div className='input-field'>
        <label>Country</label>
        <select className='dropdown-select' placeholder={'Select Country'} value={country} onChange={handleCountryChange}>
          <option value={null}>Select Country</option>
          {worldData?.countries?.map((item, index) => (
            <option value={`${item?.country}_${index}`}>{item?.country}</option>
          ))}
        </select>
      </div>

      {country && (
        <div className='input-field'>
          <label>City</label>
          <select className='dropdown-select' placeholder={'Select State'} value={state} onChange={e => setState(e.target.value)} disabled={!country}>
          <option value={null}>Select City</option>
            {cities?.map(item => (
              <option value={item?.cityname}>{item?.cityname}</option>
            ))}
            <option value={'other'}>Other</option>
          </select>
        </div>
      )}

      <div className='form_footer'></div>

      <ButtonApi onClick={handleSubmit}>Submit Application </ButtonApi>
    </animated.div>
  );
};

export default Forms;
